'use client';

import { useCallback, useEffect } from 'react';
import { defaultBasketData, ROUTES, skeletonColor } from 'constant';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  useGetPackageItemsQuery,
  useGetPackagesQuery
} from 'services/packagesApi';
import {
  useAddToShoppingCartMutation,
  useGetShoppingCartQuery,
  useRemoveGiftCardPaymentMutation,
  useUpdateShoppingCartItemMutation
} from 'services/shoppingCartApi';

import { Box, Flex, Heading, Skeleton, Text, useTheme } from '@chakra-ui/react';

import { getCustomFormattedDate } from 'utils';

import { PackageListType, ShopCartType } from 'types';

import { Button, Loader } from 'ui';
import { SubPackagesList } from '../SubPackagesList';

interface PackageDetailsPageProps {
  id: string;
}

const PackageDetailsPage = ({ id }: PackageDetailsPageProps) => {
  const {
    colors: {
      palette: {
        dashboard: {
          packages: { packagePage }
        }
      }
    }
  } = useTheme();
  const itemId = +id;

  const router = useRouter();

  const { data = defaultBasketData } = useGetShoppingCartQuery('');
  const { data: packages = [], isLoading } = useGetPackagesQuery(
    PackageListType.ACTIVE
  );
  const { data: items = [], isLoading: isPackageItemsLoading } =
    useGetPackageItemsQuery(itemId);
  const [addToShoppingCart, { isLoading: isAdding }] =
    useAddToShoppingCartMutation();
  const [updateShoppingCartItem, { isLoading: isUpdating }] =
    useUpdateShoppingCartItemMutation();
  const [removeGiftCardPayment] = useRemoveGiftCardPaymentMutation();

  const arePackagesExist = packages.length > 0;
  const parentPackage = packages.find((item) => item.id === itemId);

  const handleBack = () => {
    if (!parentPackage && arePackagesExist) {
      router.push(ROUTES.packages);
    }
  };

  useEffect(() => {
    if (!parentPackage && arePackagesExist) {
      handleBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirectToCart = useCallback(() => router.push(ROUTES.cart), [router]);

  const removeGiftCardPaymentHandler = useCallback(() => {
    if (data.payments.length > 0) {
      const pymtIds = data.payments.map((payment) => payment?.in_pymt_id);
      const deletePromises = pymtIds.map((pymtId) =>
        removeGiftCardPayment({
          in_pymt_id: pymtId,
          invoice_id: data.details.invoice_id ?? 0
        })
      );

      return Promise.allSettled(deletePromises);
    }
  }, [data.payments, data.details.invoice_id, removeGiftCardPayment]);

  const handleItemBuy = useCallback(async () => {
    const existPack = data.items.find(
      (item) => item.id === parentPackage?.tmpl_id
    );
    if (existPack?.id) {
      await updateShoppingCartItem({
        invoiceId: data.details.invoice_id ?? 0,
        cartItemId: existPack?.cart_item_id ?? 0,
        itemId: existPack?.id ?? 0,
        itemType: existPack?.item_type ?? ShopCartType.Package,
        qty: existPack.qty + 1
      })
        .then(redirectToCart)
        .then(removeGiftCardPaymentHandler);
    } else if (parentPackage?.tmpl_id) {
      await addToShoppingCart({
        item_id: parentPackage?.tmpl_id,
        item_type: ShopCartType.Package,
        store_id: parentPackage?.store_id,
        qty: 1
      })
        .then(redirectToCart)
        .then(removeGiftCardPaymentHandler);
    }
  }, [
    redirectToCart,
    parentPackage,
    data.items,
    data.details.invoice_id,
    addToShoppingCart,
    removeGiftCardPaymentHandler,
    updateShoppingCartItem
  ]);

  return (
    <Box position='relative'>
      {(isAdding || isUpdating) && <Loader absolute bg='white' />}
      <Flex mb={['37px', '30px', '22px']}>
        <Flex gap='30px' direction='column' width='100%'>
          <Heading
            fontSize={[22, 24, 28]}
            fontWeight={600}
            lineHeight={0.8}
            color={packagePage.headingColor}
            textAlign={{
              base: 'center',
              sm: 'start'
            }}
            w='100%'
          >
            Package Details
          </Heading>

          {isLoading && (
            <Skeleton
              w={{ base: 'calc(100% + 30px)', sm: 'full' }}
              borderRadius={{ base: 0, sm: 6 }}
              height={{ base: '98px', md: '84px', lg: '70px' }}
              mx={{ base: '-15px', sm: 0 }}
              variant='shine'
              startColor={skeletonColor.start}
              endColor={skeletonColor.end}
            />
          )}
          {parentPackage && (
            <Flex
              minH={{ base: '98px', md: '84px', lg: '70px' }}
              justifyContent='space-between'
              alignItems='center'
              flexWrap={{ base: 'wrap', md: 'nowrap' }}
              padding='20px'
              bg={packagePage.packageBg}
              gap={{ base: '10px', md: '50px' }}
              mx={{ base: '-15px', sm: 0 }}
              borderRadius={{ base: 0, sm: 6 }}
              alignSelf='stretch'
            >
              <Text
                fontSize={['22px', '24px']}
                color={packagePage.packageTextColor}
                lineHeight='1.2'
                maxW={{ lg: '60%' }}
              >
                {parentPackage.name}
              </Text>

              <Flex
                gap={{ base: '25px', md: '5px', lg: '25px' }}
                flexShrink={0}
                flexDirection={{
                  base: 'row',
                  md: 'column',
                  lg: 'row'
                }}
              >
                <Text
                  fontSize='16px'
                  color={packagePage.packageTextColor}
                  lineHeight='1.2'
                >
                  Purchased
                </Text>
                <Text
                  fontSize='16px'
                  color={packagePage.packageTextColor}
                  lineHeight='1.2'
                >
                  {getCustomFormattedDate(
                    parentPackage.sys_dt,
                    'MMMM D, YYYY',
                    'MMMM D, YYYY'
                  )}
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>

      <Box w='100%'>
        <SubPackagesList
          items={items}
          isLoading={isLoading || isPackageItemsLoading}
        />

        <Flex
          justifyContent='center'
          flexDirection={['column-reverse', 'row']}
          alignItems='center'
          gap={['20px', '30px']}
          mt='50px'
        >
          <Button
            variant='outline'
            width={150}
            href={ROUTES.packages}
            as={Link}
          >
            Back
          </Button>

          <Button
            width={150}
            onClick={handleItemBuy}
            isDisabled={!parentPackage}
          >
            Buy
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

export default PackageDetailsPage;
